import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";
import { trackEventOnce } from "../asset/js/analytics";
import Cookies from "js-cookie";
import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import ShowBids from "./ShowBids";
import BidImageLightbox from "./BidImageLightbox";
import { getBidImageUrl, normalizeBidImageUrl, resetImageRetryState, retryImageLoad } from "../asset/js/retryImageLoad";

function plainTextFromHtml(value) {
  if (value == null) return "";

  const source = String(value);

  if (typeof document === "undefined" || typeof DOMParser === "undefined") {
    return source.replace(/<[^>]*>/g, "").trim();
  }

  // Some older bids contain encoded rich-text tags, so decode first and then
  // convert the markup to safe plain text while retaining paragraph breaks.
  const decoder = document.createElement("textarea");
  decoder.innerHTML = source;
  const decoded = decoder.value
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\s*\/\s*(p|div|li)\s*>/gi, "\n");
  const parsed = new DOMParser().parseFromString(decoded, "text/html");

  return (parsed.body.textContent || "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function PublicShowBid() {
  const { id } = useParams();
  const pageFont = process.env.REACT_APP_FONT || "Assistant";
  const token = Cookies.get("token");
  const [authState, setAuthState] = useState(token ? "checking" : "guest");
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(!token);
  const [error, setError] = useState("");
  const [lightboxImage, setLightboxImage] = useState("");
  const [selectedImage, setSelectedImage] = useState("");

  useEffect(() => {
    trackEventOnce("tender_viewed", String(id), { tender_id: id, viewer_state: token ? "authenticated" : "guest" });
  }, [id, token]);

  useEffect(() => {
    let active = true;

    if (!token) {
      setAuthState("guest");
      return () => {
        active = false;
      };
    }

    axios
      .get(`${process.env.React_APP_BACKEND_URL}/api/profile`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 12000,
      })
      .then((res) => {
        if (active && res.status === 200) setAuthState("authenticated");
      })
      .catch((err) => {
        if (!active) return;
        // A failed profile request is not proof of logout. The global
        // ACCOUNT_BLOCKED handler remains responsible for blocked accounts.
        if (err?.response?.status === 403 && err?.response?.data?.error === "ACCOUNT_BLOCKED") { setAuthState("blocked"); return; }
        // Do not treat an expired/invalid session as authenticated.
        // The existing global ACCOUNT_BLOCKED interceptor handles blocked users.
        if (err?.response?.status === 401 || err?.response?.status === 419) {
          setAuthState("guest");
        } else {
          setAuthState("authenticated");
        }
      });

    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    let active = true;

    if (authState !== "guest") return () => {};

    setLoading(true);
    setError("");

    axios
      .get(`${process.env.React_APP_BACKEND_URL}/api/allbids`)
      .then((res) => {
        if (!active) return;
        const bids = Array.isArray(res.data?.Bids) ? res.data.Bids : [];
        const selected = bids.find(
          (bid) =>
            String(bid.id) === String(id) &&
            String(bid.status) === "1" &&
            String(bid.showbid || "yes").toLowerCase() !== "no"
        );

        if (!selected) {
          setError("המכרז אינו זמין כרגע או שכבר הוסר.");
          setPost(null);
          return;
        }

        setPost(selected);
      })
      .catch(() => {
        if (active) setError("לא הצלחנו לטעון את המכרז כרגע. אפשר לנסות שוב.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [authState, id]);

  const images = useMemo(() => {
    if (!post?.images) return [];
    return String(post.images)
      .split(",")
      .map(normalizeBidImageUrl)
      .filter(Boolean);
  }, [post]);

  useEffect(() => {
    setSelectedImage(images[0] || "");
  }, [images]);

  const skills = useMemo(() => {
    if (!post?.skills) return [];
    return String(post.skills)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }, [post]);

  const deadlineText = useMemo(() => {
    if (!post?.deadline) return "";
    const remaining = Number(post.deadline) - Date.now();
    if (!Number.isFinite(remaining) || remaining <= 0) return "המכרז הסתיים";

    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining / (1000 * 60)) % 60);
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      return `נותרו ${days} ימים ו-${hours % 24} שעות`;
    }
    if (hours > 0) return `נותרו ${hours} שעות ו-${minutes} דקות`;
    return `נותרו ${Math.max(1, minutes)} דקות`;
  }, [post]);

  const descriptionText = useMemo(
    () => plainTextFromHtml(post?.description),
    [post?.description]
  );

  const rememberTenderAndGo = (path) => {
    sessionStorage.setItem("biditAssistantReturnTo", `/showbid/${id}`);
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.location.assign(`${path}#bidit-auth-top`);
  };

  if (authState === "authenticated") return <ShowBids />;

  if (authState === "blocked") return null;

  if (authState === "checking") {
    return (
      <Box sx={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      dir="rtl"
      sx={{
        minHeight: "calc(100vh - 70px)",
        py: { xs: 2.2, md: 4.5 },
        px: { xs: 0.25, sm: 1 },
        fontFamily: pageFont,
        background:
          "linear-gradient(180deg, #F5FAFE 0%, #FFFFFF 62%)",
        "& .MuiTypography-root, & .MuiButton-root, & .MuiChip-root, & .MuiAlert-root": {
          fontFamily: pageFont,
          letterSpacing: 0,
        },
      }}
    >
    <Container maxWidth="md" sx={{ fontFamily: pageFont }}>
      {loading ? (
        <Box sx={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 3, borderRadius: 3, textAlign: "center" }}>
          <Alert severity="info" sx={{ mb: 2, textAlign: "right" }}>{error}</Alert>
          <Button component={RouterLink} to="/mybids" variant="contained">
            חזרה למכרזים שלי
          </Button>
        </Paper>
      ) : post ? (
        <Stack spacing={{ xs: 2, md: 2.5 }}>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              p: { xs: 2.2, sm: 2.8, md: 3.2 },
              borderRadius: { xs: 3.5, md: 4 },
              color: "#17394d",
              background: "linear-gradient(135deg, #FFFFFF 0%, #F4FAFE 100%)",
              border: "1px solid #DCEAF3",
              borderRight: "4px solid #1597DF",
              boxShadow: "0 12px 34px rgba(31,62,86,.08)",
              "&:after": {
                content: '\"\"',
                position: "absolute",
                width: 210,
                height: 210,
                borderRadius: "50%",
                left: -70,
                top: -105,
                background: "rgba(21,151,223,.055)",
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.4, position: "relative", zIndex: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#19a97b", boxShadow: "0 0 0 4px rgba(25,169,123,.13)" }} />
              <Typography sx={{ color: "#0877c9", fontWeight: 800, fontSize: { xs: ".9rem", md: ".98rem" } }}>
                מכרז חדש שמתאים לך
              </Typography>
            </Stack>
            <Typography component="h1" sx={{ position: "relative", zIndex: 1, fontWeight: 900, fontSize: { xs: "1.38rem", sm: "1.62rem", md: "1.78rem" }, lineHeight: 1.32, maxWidth: 680 }}>
              {post.title}
            </Typography>
            <Typography sx={{ mt: 0.8, position: "relative", zIndex: 1, color: "#647b8a", fontSize: { xs: ".94rem", md: "1rem" } }}>
              כל הפרטים שצריך כדי לבדוק אם העבודה מתאימה לך
            </Typography>
          </Box>

          {images.length > 0 && (
            <Box>
              <Box
                sx={{ width: "100%", height: { xs: 300, sm: 440 }, display: "grid", placeItems: "center", overflow: "hidden", bgcolor: "#eef4f8", borderRadius: 4, boxShadow: "0 14px 36px rgba(31,62,86,.14)", cursor: "zoom-in" }}
                role="button"
                tabIndex={0}
                aria-label="פתיחת התמונה בגלריה"
                onClick={() => setLightboxImage(selectedImage || images[0])}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setLightboxImage(selectedImage || images[0]);
                  }
                }}
              >
                <Box
                  key={selectedImage || images[0]}
                  component="img"
                  className="bidit-main-bid-image"
                  src={getBidImageUrl(selectedImage || images[0], "main")}
                  alt={post.title || "תמונת המכרז"}
                  loading="eager"
                  decoding="async"
                  onLoad={resetImageRetryState}
                  onError={retryImageLoad}
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "block",
                    objectFit: "contain",
                    p: "0 !important",
                    m: "0 !important",
                  }}
                />
              </Box>
              <Stack direction="row" spacing={1} sx={{ overflowX: "auto", py: 1.2, px: 0.3 }}>
                {images.map((image, index) => (
                  <Box
                    key={`${image}-${index}`}
                    component="button"
                    type="button"
                    onClick={() => setSelectedImage(image)}
                    aria-label={`הצגת תמונה ${index + 1}`}
                    sx={{ flex: "0 0 auto", width: { xs: 78, sm: 100 }, height: { xs: 62, sm: 76 }, p: 0, overflow: "hidden", cursor: "pointer", borderRadius: 2, bgcolor: "#eef4f8", border: (selectedImage || images[0]) === image ? "3px solid #1597DF" : "2px solid #D7E3EA" }}
                  >
                    <Box component="img" src={getBidImageUrl(image, "thumbnail")} alt="" loading="lazy" decoding="async" onLoad={resetImageRetryState} onError={retryImageLoad} sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", p: "0 !important", m: 0 }} />
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          <Paper elevation={0} sx={{ p: { xs: 2.3, md: 3.5 }, borderRadius: 4, border: "1px solid #E1EDF5", boxShadow: "0 12px 34px rgba(31,62,86,.07)" }}>
            <Stack spacing={2.4}>
              <Typography sx={{ color: "#0877c9", fontWeight: 900, fontSize: "1.18rem" }}>
                פרטי העבודה
              </Typography>
              <Typography sx={{ whiteSpace: "pre-wrap", fontSize: { xs: "1.01rem", md: "1.09rem" }, lineHeight: 1.85, color: "#263B49", fontWeight: 500 }}>
                {descriptionText}
              </Typography>

              <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
                {post.area && (
                  <Chip icon={<LocationOnOutlinedIcon />} label={post.area} sx={{ bgcolor: "#eef8ff", color: "#145f8e", fontWeight: 700, border: "1px solid #d7edfb" }} />
                )}
                {deadlineText && (
                  <Chip icon={<AccessTimeRoundedIcon />} label={deadlineText} sx={{ bgcolor: "#fff8e9", color: "#826321", fontWeight: 700, border: "1px solid #fae8ba" }} />
                )}
                {skills.map((skill) => (
                  <Chip key={skill} icon={<WorkOutlineRoundedIcon />} label={skill} sx={{ bgcolor: "#f2f0ff", color: "#5d4a98", fontWeight: 700, border: "1px solid #e3defb" }} />
                ))}
              </Stack>

            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 4 },
              borderRadius: 4,
              border: "1px solid rgba(16,139,205,.2)",
              background: "linear-gradient(145deg, #F0F9FE 0%, #FBFDFE 58%, #F2FBF7 100%)",
              textAlign: "center",
              boxShadow: "0 14px 38px rgba(8,119,201,.10)",
            }}
          >
            <Box sx={{ width: 52, height: 52, mx: "auto", mb: 1.4, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: "#fff", color: "#0b8acb", boxShadow: "0 8px 22px rgba(8,119,201,.16)" }}>
              <CheckCircleRoundedIcon sx={{ fontSize: 31 }} />
            </Box>
            <Typography sx={{ color: "#12354a", fontWeight: 900, fontSize: { xs: "1.48rem", md: "1.85rem" }, mb: 0.7 }}>
              רוצה להגיש הצעה לעבודה?
            </Typography>
            <Typography sx={{ color: "#587080", mb: 2.5, fontSize: "1.02rem" }}>
              התחבר לחשבון בעל המקצוע שלך וחזור ישירות למכרז כדי להציע מחיר.
            </Typography>

            <Stack spacing={1.2} sx={{ maxWidth: 430, mx: "auto" }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowBackRoundedIcon />}
                onClick={() => rememberTenderAndGo("/signin")}
                sx={{ py: 1.45, borderRadius: 3, fontWeight: 900, fontSize: "1.05rem", fontFamily: "inherit", background: "linear-gradient(90deg, #0877c9, #109ad7)", boxShadow: "0 10px 24px rgba(8,119,201,.25)", "&:hover": { background: "linear-gradient(90deg, #066caf, #0b8bc4)" } }}
              >
                התחברות והגשת הצעה
              </Button>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.35 }}>
                <Box sx={{ height: "1px", flex: 1, bgcolor: "rgba(53,100,126,.18)" }} />
                <Typography sx={{ color: "#607b8b", fontSize: ".9rem", fontWeight: 700, whiteSpace: "nowrap" }}>
                  לא זוכר את פרטי ההתחברות?
                </Typography>
                <Box sx={{ height: "1px", flex: 1, bgcolor: "rgba(53,100,126,.18)" }} />
              </Stack>
              <Button
                variant="outlined"
                onClick={() => rememberTenderAndGo("/forgot")}
                startIcon={<LockOutlinedIcon />}
                sx={{
                  py: 1.15,
                  borderRadius: 3,
                  color: "#0877c9",
                  borderColor: "rgba(8,119,201,.34)",
                  bgcolor: "rgba(255,255,255,.78)",
                  fontWeight: 900,
                  fontFamily: "inherit",
                  "&:hover": {
                    borderColor: "#0877c9",
                    bgcolor: "#fff",
                    boxShadow: "0 7px 18px rgba(8,119,201,.12)",
                  },
                }}
              >
                איפוס סיסמה
              </Button>
            </Stack>

            <Typography sx={{ mt: 1.2, color: "#70838f", fontSize: "0.9rem" }}>
              לאחר ההתחברות נחזיר אותך אוטומטית לעמוד הזה
            </Typography>
          </Paper>
        </Stack>
      ) : null}
      <BidImageLightbox
        images={images}
        initialImage={lightboxImage}
        open={Boolean(lightboxImage)}
        onClose={() => setLightboxImage("")}
      />
    </Container>
    </Box>
  );
}

export default PublicShowBid;
